import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { createCustomerProductList } from "./schema/accounts/create-customer-product-list";
import { getCustomerProductList } from "./schema/accounts/get-customer-product-list";
import { addReturnItem } from "./schema/add-return-item";
import { convertTokenToCustomerAuth } from "./schema/convert-token-to-customer-auth";
import {
  addProductToOrderlessDraftReturn,
  createDraftReturn,
  createOrderlessDraftReturn,
} from "./schema/create-draft-return";
import { createDraftReturnFromSource } from "./schema/create-draft-return-from-source";
import { getFulfillmentOrdersByOrder } from "./schema/get-fulfillment-orders-by-order";
import { getProductFilterById } from "./schema/get-product-filter-by-id";
import { getReturn } from "./schema/get-return";
import { getReturnCreationStatus } from "./schema/get-return-creation-status";
import { getSharedList } from "./schema/get-shared-list";
import { getSubmissionWorkflowStatus } from "./schema/get-submission-workflow-status";
import { getSuggestedVariantOptions } from "./schema/get-suggested-variant-options";
import { giftAuth } from "./schema/gift-auth";
import {
  getSignupForm,
  handleMarketingEmailUnsubscribe,
  logSignupFormAnalytics,
  submitSignupFormStep,
} from "./schema/marketing";
import {
  addNewItems,
  calculateReturnTotals,
  changeAddress,
  editPendingItemQuantity,
  editReturnableItem,
  getDraftReturn,
  getEmailDiscount,
  getFaq,
  getLocations,
  getOrderId,
  getOrders,
  getRecommendedProductsByProvider,
  getRegistrationDiscounts,
  getRegistrations,
  getSatisfactionResponse,
  getShippingEstimate,
  getStripePublicKey,
  getWarrantyEligibleProducts,
  getWarrantyRegistration,
  getWarrantyRegistrationFlow,
  isShopAppDisabled,
  rejectReturnItems,
  removeNewItems,
  removeReturnItem,
  saveSatisfactionResponse,
  setCompensationMethod,
  setProvisionType,
  setShipmentMethodInfo,
  submitReturn,
  submitWarrantyRegistration,
  toggleSelectReturnItem,
  updatePickupVariant,
  verifyAddress,
} from "./schema/schemas";
import { sendEmailAuthLink } from "./schema/send-email-auth-link";
import { shopAppTempLogin } from "./schema/shop-app-temp-login";
import { singleOrderAuth } from "./schema/single-order-auth";
import { skipAddReturnItems } from "./schema/skip-add-return-items";
import { convertSocialAuthToCustomerAuth } from "./schema/social-auth";
import { submitDraftReturn } from "./schema/submit-draft-return";

// Split up the RPC schemas to prevent the RPC definition type from getting too large for TypeScript to emit

const changeAddressRpcSchema = { changeAddress };
const editReturnableItemRpcSchema = { editReturnableItem };
const createDraftReturnRpcSchema = {
  createDraftReturn,
  createOrderlessDraftReturn,
  addProductToOrderlessDraftReturn,
  createDraftReturnFromSource,
};
const shopAppTempLoginSchema = { shopAppTempLogin };
const editPendingItemQuantityRpcSchema = { editPendingItemQuantity };
const getDraftReturnRpcSchema = { getDraftReturn };
const getShippingEstimateRpcSchema = { getShippingEstimate };
const getRecommendedProductsByProviderRpcSchema = {
  getRecommendedProductsByProvider,
};
const getStripePublicKeyRpcSchema = { getStripePublicKey };
const setShipmentMethodInfoRpcSchema = { setShipmentMethodInfo };
const setCompensationMethodRpcSchema = { setCompensationMethod };
const addReturnItemRpcSchema = { addReturnItem };
const skipAddReturnItemsRpcSchema = { skipAddReturnItems };
const removeReturnItemRpcSchema = { removeReturnItem };
const submitReturnRpcSchema = { submitReturn };
const submitDraftReturnRpcSchema = { submitDraftReturn };
const rejectReturnItemsRpcSchema = { rejectReturnItems };
const addNewItemsRpcSchema = { addNewItems };
const removeNewItemsRpcSchema = { removeNewItems };
const verifyAddressRpcSchema = { verifyAddress };
const calculateReturnTotalsRpcSchema = { calculateReturnTotals };
const getLocationsRpcSchema = { getLocations };
const getSatisfactionResponseRpcSchema = { getSatisfactionResponse };
const saveSatisfactionResponseSchema = { saveSatisfactionResponse };
const getFaqSchema = { getFaq };
const getSharedListRpcSchema = { getSharedList };
const customerProductListSchema = {
  getCustomerProductList,
  createCustomerProductList,
};
const getSubmissionWorkflowStatusSchema = { getSubmissionWorkflowStatus };
const getWarrantyEligibleProductsSchema = { getWarrantyEligibleProducts };
const getWarrantyRegistrationFlowSchema = { getWarrantyRegistrationFlow };
const getWarrantyRegistrationSchema = { getWarrantyRegistration };
const getRegistrationDiscountsSchema = { getRegistrationDiscounts };
const getEmailDiscountSchema = { getEmailDiscount };
const submitWarrantyRegistrationSchema = { submitWarrantyRegistration };
const fulfillmentOrderSchema = { getFulfillmentOrdersByOrder };
const getOrderIdSchema = { getOrderId };
const toggleSelectReturnItemSchema = { toggleSelectReturnItem };
const setProvisionTypeRpcSchema = { setProvisionType };
const getReturnRpcSchema = { getReturn };
const marketingSchema = {
  getSignupForm,
  submitSignupFormStep,
  logSignupFormAnalytics,
  handleMarketingEmailUnsubscribe,
};
const sendEmailAuthLinkSchema = { sendEmailAuthLink };
const singleOrderAuthSchema = { singleOrderAuth };
const convertTokenToCustomerAuthSchema = { convertTokenToCustomerAuth };
const convertSocialAuthToCustomerAuthSchema = {
  convertSocialAuthToCustomerAuth,
};
const getOrdersRpcSchema = { getOrders };
const isShopAppDisabledRpcSchema = { isShopAppDisabled };
const updatePickupVariantSchema = { updatePickupVariant };
const getProductFilterByIdSchema = { getProductFilterById };
const getReturnCreationStatusSchema = { getReturnCreationStatus };
const getRegistrationsSchema = { getRegistrations };
const giftAuthSchema = { giftAuth };
const getSuggestedVariantOptionsSchema = { getSuggestedVariantOptions };
type RpcDefinition = typeof changeAddressRpcSchema &
  typeof createDraftReturnRpcSchema &
  typeof editReturnableItemRpcSchema &
  typeof editPendingItemQuantityRpcSchema &
  typeof getDraftReturnRpcSchema &
  typeof getShippingEstimateRpcSchema &
  typeof getRecommendedProductsByProviderRpcSchema &
  typeof getStripePublicKeyRpcSchema &
  typeof setShipmentMethodInfoRpcSchema &
  typeof setCompensationMethodRpcSchema &
  typeof addReturnItemRpcSchema &
  typeof skipAddReturnItemsRpcSchema &
  typeof removeReturnItemRpcSchema &
  typeof submitReturnRpcSchema &
  typeof submitDraftReturnRpcSchema &
  typeof rejectReturnItemsRpcSchema &
  typeof addNewItemsRpcSchema &
  typeof removeNewItemsRpcSchema &
  typeof verifyAddressRpcSchema &
  typeof calculateReturnTotalsRpcSchema &
  typeof getLocationsRpcSchema &
  typeof getSatisfactionResponseRpcSchema &
  typeof saveSatisfactionResponseSchema &
  typeof getFaqSchema &
  typeof getSharedListRpcSchema &
  typeof customerProductListSchema &
  typeof getWarrantyEligibleProductsSchema &
  typeof getWarrantyRegistrationFlowSchema &
  typeof getWarrantyRegistrationSchema &
  typeof submitWarrantyRegistrationSchema &
  typeof fulfillmentOrderSchema &
  typeof getOrderIdSchema &
  typeof toggleSelectReturnItemSchema &
  typeof setProvisionTypeRpcSchema &
  typeof getReturnRpcSchema &
  typeof marketingSchema &
  typeof getOrdersRpcSchema &
  typeof isShopAppDisabledRpcSchema &
  typeof updatePickupVariantSchema &
  typeof getSubmissionWorkflowStatusSchema &
  typeof getProductFilterByIdSchema &
  typeof getReturnCreationStatusSchema &
  typeof getRegistrationDiscountsSchema &
  typeof getRegistrationsSchema &
  typeof getEmailDiscountSchema &
  typeof sendEmailAuthLinkSchema &
  typeof convertTokenToCustomerAuthSchema &
  typeof convertSocialAuthToCustomerAuthSchema &
  typeof shopAppTempLoginSchema &
  typeof giftAuthSchema &
  typeof singleOrderAuthSchema &
  typeof getSuggestedVariantOptionsSchema;

export const rpcDefinition: RpcDefinition = {
  ...changeAddressRpcSchema,
  ...createDraftReturnRpcSchema,
  ...editReturnableItemRpcSchema,
  ...editPendingItemQuantityRpcSchema,
  ...getDraftReturnRpcSchema,
  ...getShippingEstimateRpcSchema,
  ...getRecommendedProductsByProviderRpcSchema,
  ...getStripePublicKeyRpcSchema,
  ...setShipmentMethodInfoRpcSchema,
  ...setCompensationMethodRpcSchema,
  ...addReturnItemRpcSchema,
  ...skipAddReturnItemsRpcSchema,
  ...removeReturnItemRpcSchema,
  ...submitReturnRpcSchema,
  ...submitDraftReturnRpcSchema,
  ...rejectReturnItemsRpcSchema,
  ...addNewItemsRpcSchema,
  ...removeNewItemsRpcSchema,
  ...verifyAddressRpcSchema,
  ...calculateReturnTotalsRpcSchema,
  ...getLocationsRpcSchema,
  ...getSatisfactionResponseRpcSchema,
  ...saveSatisfactionResponseSchema,
  ...getFaqSchema,
  ...getSharedListRpcSchema,
  ...customerProductListSchema,
  ...getWarrantyEligibleProductsSchema,
  ...getWarrantyRegistrationFlowSchema,
  ...getWarrantyRegistrationSchema,
  ...submitWarrantyRegistrationSchema,
  ...getRegistrationDiscountsSchema,
  ...fulfillmentOrderSchema,
  ...getOrderIdSchema,
  ...toggleSelectReturnItemSchema,
  ...setProvisionTypeRpcSchema,
  ...getReturnRpcSchema,
  ...marketingSchema,
  ...getOrdersRpcSchema,
  ...isShopAppDisabledRpcSchema,
  ...updatePickupVariantSchema,
  ...getSubmissionWorkflowStatusSchema,
  ...getProductFilterByIdSchema,
  ...getReturnCreationStatusSchema,
  ...getEmailDiscountSchema,
  ...getRegistrationsSchema,
  ...sendEmailAuthLinkSchema,
  ...convertTokenToCustomerAuthSchema,
  ...convertSocialAuthToCustomerAuthSchema,
  ...shopAppTempLoginSchema,
  ...giftAuthSchema,
  ...singleOrderAuthSchema,
  ...getSuggestedVariantOptionsSchema,
};

export type CustomerPortalRpcDefinition = InferRpcDefinition<
  typeof rpcDefinition
>;
